import type { StateCreator } from "zustand";
import { actionNameBuilder } from "../../utilities/storeActionNameBuilder";

export type SecondarySource = "relationship" | "search";

export interface SecondaryItem {
  id: string | number;
  layerId: string;
}

export interface LoadingProgress {
  step: string;
  current: number;
  total: number;
}

export interface SecondarySelectionState {
  items: SecondaryItem[];
  source: SecondarySource | null;
  idsByLayer: Record<string, (string | number)[]>;
  itemsBbox: null | number[];
  loadingProgress: LoadingProgress | null;
}

export interface SecondarySelectionActions {
  setSecondaryItems: (items: SecondaryItem[], source: SecondarySource) => void;
  clearSecondaryItems: () => void;
  setSecondaryItemsBbox: (bbox: number[] | null) => void;
  setLoadingProgress: (progress: LoadingProgress | null) => void;
}

export interface SecondarySelectionSlice {
  secondary: SecondarySelectionState & {
    actions: SecondarySelectionActions;
  };
}

const actionName = actionNameBuilder("secondarySelection");

function computeSecondaryIdsByLayer(items: SecondaryItem[]): Record<string, (string | number)[]> {
  const map: Record<string, Set<string | number>> = {};
  for (const item of items) {
    if (!map[item.layerId]) {
      map[item.layerId] = new Set();
    }
    map[item.layerId].add(item.id);
  }

  // Convert Sets to arrays (returns deduplicated array)
  const result: Record<string, (string | number)[]> = {};
  for (const layerId in map) {
    result[layerId] = Array.from(map[layerId]);
  }
  return result;
}

/**
 * Slice creator typed generically so it composes without TS errors in the combined store.
 */
export const createSecondarySelectionSlice = <T extends SecondarySelectionSlice>(
  ...[set]: Parameters<StateCreator<T, [["zustand/devtools", never], ["zustand/immer", never]], []>>
): SecondarySelectionSlice => ({
  secondary: {
    // Initial state
    items: [],
    source: null,
    idsByLayer: {},
    itemsBbox: null,
    loadingProgress: null,

    // Actions
    actions: {
      setSecondaryItems: (items, source) =>
        set(
          (state) => {
            state.secondary.items = items;
            state.secondary.source = source;
            state.secondary.idsByLayer = computeSecondaryIdsByLayer(items);
          },
          undefined,
          actionName("setSecondaryItems")
        ),

      setSecondaryItemsBbox: (bbox) =>
        set(
          (state) => {
            state.secondary.itemsBbox = bbox;
          },
          undefined,
          actionName("setSecondaryItemsBbox")
        ),

      clearSecondaryItems: () =>
        set(
          (state) => {
            state.secondary.items = [];
            state.secondary.source = null;
            state.secondary.idsByLayer = {};
            state.secondary.itemsBbox = null;
          },
          undefined,
          actionName("clearSecondaryItems")
        ),

      setLoadingProgress: (progress) =>
        set(
          (state) => {
            state.secondary.loadingProgress = progress;
          },
          undefined,
          actionName("setLoadingProgress")
        )
    }
  }
});
